import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Settings } from 'lucide-react'

import { projectRef } from '@/features/issue'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import {
  Alert,
  Badge,
  Card,
  CardHeader,
  EmptyState,
  PageHeader,
  SkeletonRows,
  mutedTextClass,
} from '@/components/layout/ui'
import { projectsApi, tasksApi } from '@/lib/api'
import { cn } from '@/lib/cn'
import { messageFor } from '@/lib/errors'
import { formatDate, formatRelative, titleCase } from '@/lib/format'
import { queryKeys } from '@/lib/queryClient'
import { useProjects } from '@/lib/queries'
import { useWorkspace } from '@/lib/workspace'
import type { TaskPriority, TaskStatus } from '@/lib/types'

const STATUS_TONE: Record<TaskStatus, 'neutral' | 'brand' | 'success'> = {
  pending: 'neutral',
  in_progress: 'brand',
  done: 'success',
}

const PRIORITY_TONE: Record<TaskPriority, 'neutral' | 'warning' | 'danger'> = {
  low: 'neutral',
  medium: 'warning',
  high: 'danger',
}

/**
 * `/{slug}/projects/{id}` — a project at a glance.
 *
 * It reads the frozen v1 endpoints, which are the ones the iPad and the MCP
 * server already write to, so a project created anywhere shows up here with no
 * migration. The board itself is a separate surface; this is the summary the
 * shell needs to be navigable.
 */
export default function ProjectPage() {
  const { projectId: routeRef = '' } = useParams<{ projectId: string }>()
  const workspace = useWorkspace()
  const [params] = useSearchParams()
  const highlighted = params.get('task')

  // The URL carries a project KEY ("TW") wherever one exists and a UUID only
  // for legacy rows that never got one. The v1 endpoints below address projects
  // by UUID alone, so the key is resolved against the workspace's project list
  // before anything is fetched.
  const projectList = useProjects(workspace.slug, workspace.membership.id)
  const projectId = useMemo(() => {
    if (!routeRef) return ''
    const wanted = routeRef.toLowerCase()
    const match = (projectList.data ?? []).find(
      (candidate) =>
        candidate.id.toLowerCase() === wanted ||
        candidate.project_key?.toLowerCase() === wanted,
    )
    if (match) return match.id
    // Still loading: hold the fetch rather than firing a doomed lookup.
    return projectList.isPending ? '' : routeRef
  }, [projectList.data, projectList.isPending, routeRef])

  const project = useQuery({
    queryKey: queryKeys.project(projectId),
    queryFn: () => projectsApi.get(projectId),
    enabled: projectId.length > 0,
  })

  const sprints = useQuery({
    queryKey: queryKeys.sprints(projectId),
    queryFn: () => projectsApi.sprints(projectId),
    enabled: projectId.length > 0,
  })

  const tasks = useQuery({
    queryKey: queryKeys.tasks({ project_id: projectId }),
    queryFn: ({ signal }) => tasksApi.list({ project_id: projectId, per_page: 25 }, { signal }),
    enabled: projectId.length > 0,
  })

  if (project.isError) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-10">
        <Alert tone="danger" title="Project unavailable">
          {messageFor(project.error)}
        </Alert>
        <Link
          to={workspace.path()}
          className="mt-4 inline-block text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
        >
          Back to overview
        </Link>
      </div>
    )
  }

  // The v1 project endpoint is scoped to every workspace the caller belongs to,
  // so a project id from another workspace resolves. Refusing to render it here
  // keeps the URL's workspace honest.
  const foreign =
    project.data?.organization_id != null &&
    project.data.organization_id !== workspace.membership.id

  if (foreign) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-10">
        <Alert tone="warning" title="Not in this workspace">
          This project belongs to another workspace. Switch workspaces to open it.
        </Alert>
      </div>
    )
  }

  const rows = tasks.data?.data ?? []
  const counts = rows.reduce<Record<TaskStatus, number>>(
    (acc, task) => {
      acc[task.status] += 1
      return acc
    },
    { pending: 0, in_progress: 0, done: 0 },
  )

  return (
    <div className="mx-auto max-w-4xl px-6 py-10">
      <PageHeader
        title={project.isPending ? 'Loading…' : project.data.name}
        description={project.data?.description || undefined}
        action={
          <div className="flex items-center gap-2">
            {project.data?.is_default ? <Badge>Default project</Badge> : null}
            {project.data ? (
              // projectRef, not the id in the URL: the settings route is keyed
              // on the project KEY where one exists, and falls back to the id
              // for older projects that never got one.
              <Link
                to={workspace.path(`/projects/${projectRef(project.data)}/settings`)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 px-2.5 py-1.5 text-sm font-medium text-ink-700 transition hover:border-ink-300 hover:bg-ink-50 dark:border-ink-700 dark:text-ink-200 dark:hover:bg-ink-900"
              >
                <Settings className="size-4" aria-hidden="true" />
                Settings
              </Link>
            ) : null}
          </div>
        }
      />

      <dl className="mb-8 grid grid-cols-3 gap-4">
        <Stat label="To do" value={counts.pending} pending={tasks.isPending} />
        <Stat label="In progress" value={counts.in_progress} pending={tasks.isPending} />
        <Stat label="Done" value={counts.done} pending={tasks.isPending} />
      </dl>

      <Card>
        <CardHeader
          title="Issues"
          description={
            tasks.data ? `Showing ${rows.length} of ${tasks.data.total}` : 'Most recent first'
          }
        />
        {tasks.isPending ? (
          <div className="p-5">
            <SkeletonRows rows={4} />
          </div>
        ) : tasks.isError ? (
          <div className="p-5">
            <Alert tone="danger">{messageFor(tasks.error)}</Alert>
          </div>
        ) : rows.length === 0 ? (
          <EmptyState title="No issues yet" description="Issues created anywhere in Taskly land here." />
        ) : (
          <ul className="divide-y divide-ink-200 dark:divide-ink-800">
            {rows.map((task) => (
              <li
                key={task.id}
                className={cn(
                  'flex flex-wrap items-center gap-3 px-5 py-3.5',
                  task.id === highlighted && 'bg-brand-50 dark:bg-brand-950',
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{task.title}</span>
                  <span className={cn('mt-0.5 block text-xs', mutedTextClass)}>
                    {task.issue_key ? `${task.issue_key} · ` : ''}
                    {task.assigned_to ? `${task.assigned_to} · ` : ''}
                    updated {formatRelative(task.updated_at)}
                  </span>
                </span>
                <Badge tone={PRIORITY_TONE[task.priority]}>{titleCase(task.priority)}</Badge>
                <Badge tone={STATUS_TONE[task.status]}>{titleCase(task.status)}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="mt-6">
        <CardHeader title="Sprints" />
        {sprints.isPending ? (
          <div className="p-5">
            <SkeletonRows rows={2} />
          </div>
        ) : sprints.isError ? (
          <div className="p-5">
            <Alert tone="danger">{messageFor(sprints.error)}</Alert>
          </div>
        ) : sprints.data.length === 0 ? (
          <EmptyState title="No sprints" description="Sprints are a Taskly Pro feature." />
        ) : (
          <ul className="divide-y divide-ink-200 dark:divide-ink-800">
            {sprints.data.map((sprint) => (
              <li key={sprint.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{sprint.name}</span>
                  <span className={cn('mt-0.5 block text-xs', mutedTextClass)}>
                    {formatDate(sprint.start_date)} – {formatDate(sprint.end_date)}
                  </span>
                </span>
                <Badge tone={sprint.status === 'active' ? 'brand' : 'neutral'}>
                  {titleCase(sprint.status)}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}

function Stat({ label, value, pending }: { label: string; value: number; pending: boolean }) {
  return (
    <div className="rounded-card border border-ink-200 bg-white px-4 py-3 dark:border-ink-800 dark:bg-ink-900">
      <dt className={cn('text-xs', mutedTextClass)}>{label}</dt>
      <dd className="mt-1 text-2xl font-semibold tabular-nums">
        {pending ? <span className="text-ink-300 dark:text-ink-700">—</span> : value}
      </dd>
    </div>
  )
}
